import { Component, type ErrorInfo, type ReactNode } from 'react';
import { crashMessage, diagnostics } from './crash';
import { AppButton } from '@core/designsystem';
import { telemetry } from '@core/telemetry';

/**
 * Ловит исключение при отрисовке и показывает его вместо белизны.
 *
 * До 15.09.2026 в приложении не было ни одного такого рубежа, и любая ошибка внутри
 * экрана — в списке дел, в карточке, в календаре — снимала всё дерево React: юрист
 * получал белый лист, а мы не получали ничего. Разбор случая — PITFALLS §18.
 *
 * **Класс, а не хук.** Другого способа поймать ошибку отрисовки React не даёт: ни
 * `useEffect`, ни `try` вокруг разметки её не видят. Это единственный класс-компонент
 * в проекте, и других не будет.
 *
 * Причина показывается **дословно**: экран нужен ради неё одной. Рядом —
 * `lastResort.ts` на случай, когда падение случилось раньше, чем React успел
 * смонтировать этот рубеж.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { message: string | null }> {
  state: { message: string | null } = { message: null };

  static getDerivedStateFromError(reason: unknown): { message: string } {
    return { message: crashMessage(reason) };
  }

  /*
   * В консоль — потому что это единственное место, где остаётся стек.
   *
   * На экран стек не идёт: он длиннее экрана телефона и не читается. Но подключённый
   * к компьютеру телефон отдаёт консоль через веб-инспектор Safari, и тогда разбор
   * становится делом минуты.
   */
  componentDidCatch(reason: Error, info: ErrorInfo): void {
    /*
     * Считается здесь, а не в `render`: `componentDidCatch` вызывается **один раз
     * на ошибку**, а отрисовка экрана падения может повториться от любой перерисовки
     * родителя — и тогда одно падение считалось бы за пять.
     */
    telemetry.record('appCrashed');
    console.error('Упала отрисовка:', reason, info.componentStack);
  }

  render(): ReactNode {
    const { message } = this.state;
    if (message === null) return this.props.children;

    return (
      <div className="crash" role="alert">
        <h1 className="crash__title">Экран не открылся</h1>
        <p className="crash__text">
          Дела и документы на месте — они лежат на устройстве, и эта ошибка их не трогает.
        </p>
        <p className="crash__reason">{message}</p>
        <p className="crash__facts">{diagnostics(__APP_VERSION__)}</p>
        <p className="crash__text">
          Покажите этот экран разработчику: по нему видно причину. Снимок делается кнопками
          громкости и боковой.
        </p>
        <div className="crash__button">
          <AppButton onClick={() => window.location.reload()}>Попробовать снова</AppButton>
        </div>
      </div>
    );
  }
}
